import {
  DrawerAnalyticsProperties,
  DrawerAnalyticsProvider
} from '@dolfin/ui/stories/DrawerAnalytics'
import posthog from 'posthog-js'
import { PropsWithChildren, useCallback } from 'react'

type DrawerAnalyticsOrganization = {
  id?: number | string | null
  name?: string | null
}

interface WebDrawerAnalyticsProviderProps extends PropsWithChildren {
  isImpersonating: boolean
  selectedOrganization?: DrawerAnalyticsOrganization | null
}

const getEnvironment = () =>
  process.env.NEXT_PUBLIC_VERCEL_ENV ?? process.env.NODE_ENV ?? null

const getCurrentUserId = () => {
  try {
    return typeof (posthog as { get_distinct_id?: () => string }).get_distinct_id ===
      'function'
      ? (posthog as { get_distinct_id: () => string }).get_distinct_id()
      : null
  } catch {
    return null
  }
}

const getWindowLocationProperties = (): DrawerAnalyticsProperties => {
  if (typeof window === 'undefined') {
    return {}
  }

  return {
    pathname: window.location.pathname,
    search: window.location.search
  }
}

export const WebDrawerAnalyticsProvider = ({
  children,
  isImpersonating,
  selectedOrganization
}: WebDrawerAnalyticsProviderProps) => {
  const getBaseProperties = useCallback(
    () => ({
      app: 'web',
      surface: 'admin',
      environment: getEnvironment(),
      ...getWindowLocationProperties(),
      organization_id: selectedOrganization?.id ?? null,
      organization_name: selectedOrganization?.name ?? null,
      user_id: getCurrentUserId()
    }),
    [selectedOrganization?.id, selectedOrganization?.name]
  )

  const handleCapture = useCallback(
    (eventName: string, properties: DrawerAnalyticsProperties) => {
      if (isImpersonating) {
        return
      }

      posthog.capture(eventName, {
        ...properties,
        $groups:
          selectedOrganization?.id != null
            ? {
                organization: String(selectedOrganization.id)
              }
            : undefined
      })
    },
    [isImpersonating, selectedOrganization?.id]
  )

  return (
    <DrawerAnalyticsProvider
      getBaseProperties={getBaseProperties}
      onCapture={handleCapture}>
      {children}
    </DrawerAnalyticsProvider>
  )
}
